import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Heart, ShieldCheck, CheckCircle2, XCircle, QrCode } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { PayPalLogo, VenmoLogo, AppleLogo } from "@/components/PaymentIcons";

const QUICK = [3, 5, 10, 20];
const FEE_RATE = 0.03;

export default function TipPage() {
  const { username } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");

  const [profile, setProfile] = useState(null);
  const [status, setStatus] = useState("loading");
  const [amount, setAmount] = useState(5);
  const [custom, setCustom] = useState("");
  const [processing, setProcessing] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const [message, setMessage] = useState("");
  const [wall, setWall] = useState([]);

  // payment result state
  const [payState, setPayState] = useState(sessionId ? "checking" : null);
  const [payResult, setPayResult] = useState(null);

  useEffect(() => {
    api
      .get(`/u/${username}`)
      .then(({ data }) => {
        setProfile(data);
        setStatus("ready");
      })
      .catch(() => setStatus("notfound"));
    api.get(`/u/${username}/tips`).then(({ data }) => setWall(data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username]);

  const pollStatus = useCallback(
    async (sid, attempts = 0) => {
      if (attempts >= 6) {
        setPayState("timeout");
        return;
      }
      try {
        const { data } = await api.get(`/tips/status/${sid}`);
        if (data.payment_status === "paid") {
          setPayResult(data);
          setPayState("success");
          return;
        }
        if (data.status === "expired") {
          setPayState("expired");
          return;
        }
        setTimeout(() => pollStatus(sid, attempts + 1), 2000);
      } catch {
        setPayState("error");
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    if (sessionId) {
      setPayState("checking");
      pollStatus(sessionId);
    }
  }, [sessionId, pollStatus]);

  const tipValue = () => {
    const c = parseFloat(custom);
    if (custom && !isNaN(c) && c > 0) return c;
    return amount;
  };
  const fee = () => Math.round(tipValue() * FEE_RATE * 100) / 100;
  const total = () => (tipValue() + fee()).toFixed(2);

  const startCheckout = async () => {
    setProcessing(true);
    try {
      const { data } = await api.post("/tips/checkout", {
        username: profile.username,
        tip_amount: tipValue(),
        message: message.trim(),
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setProcessing(false);
    }
  };

  const clearSession = () => {
    setPayState(null);
    setPayResult(null);
    setSearchParams({});
  };

  if (status === "loading")
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#050505]">
        <Loader2 className="h-8 w-8 animate-spin text-[#00E676]" />
      </div>
    );

  if (status === "notfound")
    return (
      <div className="App min-h-screen bg-[#050505]">
        <Navbar />
        <div className="mx-auto flex max-w-lg flex-col items-center px-5 py-32 text-center">
          <h1 className="font-display text-4xl font-black tracking-tight">Nobody here.</h1>
          <p className="mt-4 text-zinc-400">
            We couldn't find <span className="font-mono-tip text-white">@{username}</span>.
          </p>
          <button
            data-testid="notfound-home-btn"
            onClick={() => navigate("/")}
            className="mt-8 rounded-full bg-[#00E676] px-6 py-3 font-bold text-black active:scale-95"
          >
            Back to search
          </button>
        </div>
      </div>
    );

  const handles = profile.handles || {};
  const hasHandles = handles.venmo || handles.paypal || handles.applepay;

  // Payment result overlays
  if (payState && payState !== null) {
    return (
      <div className="App min-h-screen bg-[#050505]">
        <Navbar />
        <div className="mx-auto flex max-w-lg flex-col items-center px-5 py-24 text-center">
          {payState === "checking" && (
            <>
              <Loader2 className="h-12 w-12 animate-spin text-[#00E676]" />
              <h1 className="mt-6 font-display text-3xl font-black tracking-tight">
                Confirming your tip…
              </h1>
              <p className="mt-2 text-zinc-400">Hang tight, this only takes a second.</p>
            </>
          )}
          {payState === "success" && (
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="flex flex-col items-center"
              data-testid="tip-success"
            >
              <CheckCircle2 className="h-16 w-16 text-[#00E676]" />
              <h1 className="mt-6 font-display text-4xl font-black tracking-tight">Tip sent! 🎉</h1>
              <p className="mt-3 text-zinc-300">
                You tipped{" "}
                <span className="font-bold text-white">${payResult?.tip_amount?.toFixed(2)}</span> to{" "}
                {payResult?.recipient_name}.
              </p>
              <p className="mt-1 text-sm text-zinc-500">
                Includes a ${payResult?.service_fee?.toFixed(2)} service fee.
              </p>
              {payResult?.message && (
                <p
                  data-testid="success-message"
                  className="mt-5 max-w-xs rounded-2xl border border-white/10 bg-[#0F0F0F] px-5 py-4 text-sm italic text-zinc-300"
                >
                  “{payResult.message}”
                </p>
              )}
              <button
                data-testid="success-done-btn"
                onClick={clearSession}
                className="mt-8 rounded-full bg-[#00E676] px-8 py-3 font-bold text-black active:scale-95"
              >
                Done
              </button>
            </motion.div>
          )}
          {["expired", "timeout", "error"].includes(payState) && (
            <div className="flex flex-col items-center" data-testid="tip-failed">
              <XCircle className="h-16 w-16 text-red-400" />
              <h1 className="mt-6 font-display text-3xl font-black tracking-tight">
                {payState === "expired" ? "Session expired" : "Something went wrong"}
              </h1>
              <p className="mt-2 text-zinc-400">Your card was not charged. Please try again.</p>
              <button
                data-testid="failed-retry-btn"
                onClick={clearSession}
                className="mt-8 rounded-full bg-[#00E676] px-8 py-3 font-bold text-black active:scale-95"
              >
                Try again
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="App min-h-screen bg-[#050505]">
      <Navbar />
      <div className="mx-auto max-w-lg px-5 py-10">
        <button
          data-testid="tip-back-btn"
          onClick={() => navigate("/")}
          className="mb-8 inline-flex items-center gap-2 text-sm text-zinc-400 transition-colors hover:text-white"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </button>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          {/* Recipient */}
          <div className="flex flex-col items-center text-center">
            <div className="h-28 w-28 overflow-hidden rounded-full border-2 border-[#00E676]/40 bg-zinc-800">
              {profile.avatar_url ? (
                <img src={profile.avatar_url} alt={profile.name} className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full w-full items-center justify-center font-display text-4xl font-black text-zinc-600">
                  {profile.name?.[0]?.toUpperCase()}
                </div>
              )}
            </div>
            <h1 data-testid="tip-recipient-name" className="mt-5 font-display text-3xl font-black tracking-tight">
              {profile.name}
            </h1>
            <p className="mt-1 font-mono-tip text-sm text-[#00E676]">@{profile.username}</p>
            {profile.job_title && (
              <p className="mt-3 text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">{profile.job_title}</p>
            )}
            {profile.bio && <p className="mt-4 max-w-xs leading-relaxed text-zinc-400">{profile.bio}</p>}

            {hasHandles && (
              <div className="mt-5 flex items-center gap-3 text-zinc-500">
                {handles.venmo && <VenmoLogo className="h-5 w-5" />}
                {handles.paypal && <PayPalLogo className="h-5 w-5" />}
                {handles.applepay && <AppleLogo className="h-5 w-5" />}
                <span className="text-xs">Gets paid out via their linked accounts</span>
              </div>
            )}
          </div>

          {/* Amount selector */}
          <div className="mt-12">
            <p className="mb-4 text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Choose a tip</p>
            <div className="grid grid-cols-4 gap-3">
              {QUICK.map((a) => {
                const active = !custom && amount === a;
                return (
                  <button
                    key={a}
                    data-testid={`tip-amount-${a}`}
                    onClick={() => {
                      setAmount(a);
                      setCustom("");
                    }}
                    className={`rounded-2xl border py-5 font-display text-2xl font-bold transition-colors ${
                      active
                        ? "border-[#00E676] bg-[#00E676]/10 text-[#00E676] shadow-[0_0_20px_rgba(0,230,118,0.15)]"
                        : "border-white/10 bg-[#0F0F0F] text-white hover:border-white/25"
                    }`}
                  >
                    ${a}
                  </button>
                );
              })}
            </div>

            <div className="mt-3 flex items-center gap-3 rounded-2xl border border-white/10 bg-[#0F0F0F] px-5 py-4 focus-within:border-[#00E676]">
              <span className="font-display text-2xl font-bold text-zinc-500">$</span>
              <input
                data-testid="tip-custom-amount"
                type="number"
                min="1"
                step="1"
                value={custom}
                onChange={(e) => setCustom(e.target.value)}
                placeholder="Custom amount"
                className="w-full bg-transparent font-display text-2xl font-bold text-white placeholder:text-base placeholder:font-medium placeholder:text-zinc-600 focus:outline-none"
              />
            </div>
          </div>

          {/* Comment */}
          <div className="mt-8">
            <label className="mb-3 block text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
              Add a note <span className="text-zinc-600">(optional)</span>
            </label>
            <textarea
              data-testid="tip-message-input"
              value={message}
              onChange={(e) => setMessage(e.target.value.slice(0, 280))}
              rows={2}
              placeholder="Say thanks! e.g. Great service today 🙌"
              className="w-full resize-none rounded-2xl border border-white/10 bg-[#0F0F0F] px-5 py-4 text-base text-white placeholder:text-zinc-600 focus:border-[#00E676] focus:outline-none"
            />
            <div className="mt-1 text-right text-xs text-zinc-600">{message.length}/280</div>
          </div>

          {/* Breakdown */}
          <div className="mt-4 space-y-2 rounded-2xl border border-white/10 bg-[#0F0F0F] p-5">
            <div className="flex items-center justify-between text-zinc-400">
              <span>Tip</span>
              <span data-testid="breakdown-tip" className="font-mono-tip text-white">
                ${tipValue().toFixed(2)}
              </span>
            </div>
            <div className="flex items-center justify-between text-zinc-400">
              <span>Service fee (3%)</span>
              <span data-testid="breakdown-fee" className="font-mono-tip text-white">
                ${fee().toFixed(2)}
              </span>
            </div>
            <div className="my-2 h-px bg-white/10" />
            <div className="flex items-center justify-between">
              <span className="font-semibold text-white">Total</span>
              <span data-testid="breakdown-total" className="font-mono-tip text-lg font-bold text-[#00E676]">
                ${total()}
              </span>
            </div>
          </div>

          <button
            data-testid="tip-pay-btn"
            onClick={startCheckout}
            disabled={processing || tipValue() < 1}
            className="mt-6 flex h-14 w-full items-center justify-center gap-2 rounded-full bg-[#00E676] font-display text-lg font-bold text-black transition-transform active:scale-[0.98] disabled:opacity-60"
          >
            {processing ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <>Tip ${total()} securely</>
            )}
          </button>

          <p className="mt-6 flex items-center justify-center gap-1.5 text-center text-xs text-zinc-600">
            <ShieldCheck className="h-3.5 w-3.5 text-[#00E676]" /> Secured by Stripe. Tips are paid out
            to {profile.name?.split(" ")[0]}.
          </p>
          <p className="mt-2 flex items-center justify-center gap-1.5 text-center text-xs text-zinc-600">
            <Heart className="h-3.5 w-3.5 text-[#00E676]" fill="currentColor" /> A 3% service fee keeps TipNu
            running.
          </p>

          {/* Tips wall — social proof */}
          {wall.length > 0 && (
            <div className="mt-12" data-testid="tips-wall">
              <p className="mb-4 text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
                Recent thanks 💚
              </p>
              <div className="space-y-3">
                {wall.map((w, i) => (
                  <div key={`${w.created_at}-${i}`} className="rounded-2xl border border-white/10 bg-[#0F0F0F] p-4">
                    <div className="flex items-center justify-between">
                      <span className="font-display text-sm font-bold text-[#00E676]">
                        ${Number(w.tip_amount).toFixed(2)} tip
                      </span>
                      {w.created_at && (
                        <span className="text-xs text-zinc-600">
                          {new Date(w.created_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                    <p className="mt-2 break-words text-sm italic text-zinc-300">“{w.message}”</p>
                    {w.reply && (
                      <div className="mt-3 border-l-2 border-[#00E676]/40 pl-3">
                        <p className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">
                          {profile.name?.split(" ")[0]} replied
                        </p>
                        <p className="mt-1 break-words text-sm text-zinc-400">{w.reply}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Scan to tip QR */}
          <div className="mt-10 flex flex-col items-center">
            <button
              data-testid="toggle-qr-btn"
              onClick={() => setShowQr((v) => !v)}
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm font-semibold text-zinc-300 transition-colors hover:bg-white/5"
            >
              <QrCode className="h-4 w-4 text-[#00E676]" />
              {showQr ? "Hide QR code" : "Show QR to share"}
            </button>
            {showQr && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-5 flex flex-col items-center"
                data-testid="tip-page-qr"
              >
                <div className="rounded-2xl bg-white p-4">
                  <QRCodeCanvas
                    value={typeof window !== "undefined" ? window.location.href.split("?")[0] : ""}
                    size={180}
                    bgColor="#ffffff"
                    fgColor="#050505"
                    level="H"
                  />
                </div>
                <p className="mt-3 max-w-xs text-center text-xs text-zinc-500">
                  Point a phone camera here to open {profile.name?.split(" ")[0]}'s tip page — no typing.
                </p>
              </motion.div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
